import { AKComponent } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {AKComponent<Refs>}
 */
class CartItemsComponent extends AKComponent {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Repère le scroll du corps du drawer panier ouvert (pour éviter un saut visuel après morph).
   * @returns {{ mode: 'anchor', anchorTop: number } | { mode: 'height', scrollTop: number, scrollHeight: number } | null}
   */
  #captureCartDrawerBodyScroll() {
    const drawer =
      document.getElementById('cart-drawer') ?? document.querySelector('cart-drawer');
    const dialog = drawer?.querySelector('dialog');
    if (!drawer || !dialog || !dialog.open) return null;

    const body = drawer.querySelector('.c-drawer__body');
    if (!(body instanceof HTMLElement)) return null;

    const anchor = body.querySelector('.c-cart-drawer__recommendations');
    if (anchor instanceof HTMLElement) {
      return {
        mode: 'anchor',
        anchorTop: anchor.getBoundingClientRect().top - body.getBoundingClientRect().top,
      };
    }

    return {
      mode: 'height',
      scrollTop: body.scrollTop,
      scrollHeight: body.scrollHeight,
    };
  }

  /**
   * Compense le changement de hauteur du contenu après mise à jour du panier (drawer ouvert).
   * @param {{ mode: 'anchor', anchorTop: number } | { mode: 'height', scrollTop: number, scrollHeight: number } | null} snapshot
   */
  #restoreCartDrawerBodyScroll(snapshot) {
    if (!snapshot) return;

    const apply = () => {
      const drawer =
        document.getElementById('cart-drawer') ?? document.querySelector('cart-drawer');
      const dialog = drawer?.querySelector('dialog');
      if (!drawer || !dialog || !dialog.open) return;

      const body = drawer.querySelector('.c-drawer__body');
      if (!(body instanceof HTMLElement)) return;

      if (snapshot.mode === 'anchor') {
        const anchor = body.querySelector('.c-cart-drawer__recommendations');
        if (!(anchor instanceof HTMLElement)) return;
        const after = anchor.getBoundingClientRect().top - body.getBoundingClientRect().top;
        body.scrollTop += after - snapshot.anchorTop;
        return;
      }

      body.scrollTop = snapshot.scrollTop + (body.scrollHeight - snapshot.scrollHeight);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(apply);
    });
  }

  /**
   * @template T
   * @param {() => T | Promise<T>} fn
   * @returns {Promise<T>}
   */
  async #withCartDrawerScrollPreserved(fn) {
    const snapshot = this.#captureCartDrawerBodyScroll();
    try {
      return await fn();
    } finally {
      this.#restoreCartDrawerBodyScroll(snapshot);
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        const scrollSnapshot = this.#captureCartDrawerBodyScroll();
        await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        this.#restoreCartDrawerBodyScroll(scrollSnapshot);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    // Find the quantity selector by data-cart-line attribute instead of array index
    const quantityInput = this.querySelector(`input[data-cart-line="${line}"]`);

    if (!quantityInput) {
      console.error('Quantity input not found for line', line);
      return;
    }

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) {
      console.error('Cart item error not found for line', line);
      return;
    }

    if (!(cartItemErrorContainer instanceof HTMLElement)) {
      console.error('Cart item error container not found for line', line);
      return;
    }

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('u-d-none');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      await this.#withCartDrawerScrollPreserved(async () => {
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
      });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      await this.#withCartDrawerScrollPreserved(async () => {
        await morphSection(this.sectionId, cartItemsHtml);
      });
    } else {
      await this.#withCartDrawerScrollPreserved(async () => {
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
      });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
